<template>
  <div class="px-6 pb-6 leading-normal">
    <h3 class="text-xl pb-4 border-b">Items:</h3>
    <p v-if="!basketItems || basketItems.length < 1" class="py-6">There are no items currently in your basket. <a class="underline cursor-pointer" @click="$emit('continueShopping')">Continue shopping</a></p>
    <table v-else class="w-full">
      <tbody>
        <template v-for="(item, i) in basketItems">
          <tr :key="i" :class="{'border-t': i > 0}">
            <td class="text-left mb-4 pt-6 pr-4" :class="{'pb-6': !item.artwork_item_price && !item.delivery_item_price}">
              <h4 class="text-base font-bold mb-1">{{ item.sale_item_category.name + ' - ' + item.sale_item.name }}</h4>
              <p class="text-sm font-bold mb-6">Quantity: {{ item.quantity }}</p>
              <p v-if="item.print_spec" class="text-sm">{{ item.print_spec.name }}</p>
            </td>
            <td class="align-top text-right text-sm mb-6 pt-6 px-4 leading-loose" :class="{'pb-6': !item.artwork_item_price && !item.delivery_item_price}">
              {{ item.item_quote | gbp }}
            </td>
            <td class="align-top text-lg mb-6 pt-6 pr-2 whitespace-no-wrap">
              <i @click="viewItem(item)" class="far fa-edit cursor-pointer" />
            </td>
            <td class="align-top text-lg mb-6 pt-6 whitespace-no-wrap" :class="{'pb-6': !item.artwork_item_price && !item.delivery_item_price}">
              <loader v-if="updatingItem" />
              <i v-else @click="showDeleteModal = true; selectedItem = item" class="fas fa-trash-alt cursor-pointer" title="Delete" />
              <deletion-modal
                v-if="showDeleteModal"
                @confirmed="$emit('removeFromBasket', selectedItem)"
                @leave="showDeleteModal = false"
                @dismissed="showDeleteModal = false"
                confirm-label="Yes, remove it.">
                <p class="text-center text-sm">Are you sure you wish to remove the print item from your basket?</p>
              </deletion-modal>
            </td>
            <td class="align-top text-lg mb-6 pt-6 whitespace-no-wrap" v-if="profile_v2.type == 0 || profile_v2.type == 1">
              <div class="px-2 bg-gray-300 hover:bg-gray-400 rounded cursor-pointer relative group inline-block">
                <i class="fas fa-fw fa-ellipsis-h"></i>
                <div class="hidden group-hover:block absolute top-0 right-full border whitespace-no-wrap bg-white shadow-md text-left">
                  <div class="px-3 py-2 hover:bg-gray-200 cursor-pointer border-b" @click="viewEditPrice('item',item)">
                    Edit New Item Price
                  </div>
                </div>
              </div>
            </td>
          </tr>
          <tr v-if="item.artwork_item_price" :key="i + '-artwork-' + item.artwork_item_price.id">
            <td class="text-left mb-4 pt-6 pr-4" :class="{'pb-6': !item.delivery_item_price}">
              <h4 class="text-base font-bold mb-1">Artwork Creation</h4>
            </td>
            <td class="align-top text-right text-sm mb-6 pt-6 px-4 leading-loose" :class="{'pb-6': !item.delivery_item_price}">
              {{ item.artwork_quote | gbp }}
            </td>
            <td></td>
            <td></td>
            <td>
              <div class="px-2 bg-gray-300 hover:bg-gray-400 rounded cursor-pointer relative group inline-block" v-if="profile_v2.type == 0 || profile_v2.type == 1">
                <i class="fas fa-fw fa-ellipsis-h"></i>
                <div class="hidden group-hover:block absolute top-0 right-full border whitespace-no-wrap bg-white shadow-md text-left">
                  <div class="px-3 py-2 hover:bg-gray-200 cursor-pointer border-b" @click="viewEditPrice('artwork',item)">
                    Edit New Artwork Price
                  </div>
                </div>
              </div>
            </td>
          </tr>
          <tr v-if="item.delivery_item_price" :key="i + '-delivery-' + item.delivery_item_price.id">
            <td class="text-left mb-4 py-6 pr-4">
              <h4 class="text-base font-bold mb-1">Delivery</h4>
            </td>
            <td class="align-top text-right text-sm mb-6 py-6 px-4 leading-loose">
              {{ item.delivery_quote | gbp }}
            </td>
            <td></td>
            <td></td>
            <td>
              <div class="px-2 bg-gray-300 hover:bg-gray-400 rounded cursor-pointer relative group inline-block" v-if="profile_v2.type == 0 || profile_v2.type == 1">
                <i class="fas fa-fw fa-ellipsis-h"></i>
                <div class="hidden group-hover:block absolute top-0 right-full border whitespace-no-wrap bg-white shadow-md text-left">
                  <div class="px-3 py-2 hover:bg-gray-200 cursor-pointer border-b" @click="viewEditPrice('delivery',item)">
                    Edit New Artwork Price
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <modal v-if="showEditPriceModal" @leave="showEditPriceModal = false" show-close-btn width="320px">
        <h2 class="font-bold mb-4">Edit Item Price</h2>
        <div v-if="salesOrderId">
          Please open the related sales order to make price changes.
          <div class="flex justify-end">
            <primary-btn @click="openSalesOrder" :loading="loadingItem">
              <i class="fas fa-coins"></i> View Sales Order
            </primary-btn>
          </div>
        </div>
        <div v-else-if="discountForm.price_type_id !== null">
          <h5 class="mb-3 text-sm">Original {{ discountTypeName }} Price: {{ discountOriginalPrice || 0 | gbp }}</h5>
          <p class="mb-6 text-sm">A discount entered here will be recorded on the invoice.</p>
          <select-input
            v-model="discountForm.type_id"
            label="Discount Type"
            :options="discountType"
            placeholder="[Please Select]"
            :error="errorBag.get('type_id')"
          />
          <input type="number" step="0.01" min="0.01" class="select bg-white flex flex-row rounded text-xs text-black mb-6" v-if="discountForm.type_id === 1 || discountForm.type_id === 2" v-model="discountForm.value" :error="errorBag.get('value')" :class-name="discountForm.type_id === 2 ? 'w-full mb-2' : 'w-full mb-6'">
          <!-- <p v-if="discountForm.type_id === 2" class="mt-1 mb-6 text-sm">New order line net price: <strong>{{ calculatedDiscountPrice | gbp }}</strong></p> -->
          <textarea-input
            label="Please note why a discount has been given"
            v-model="discountForm.notes"
            :rows="3"
            :error="errorBag.get('notes')"
          />
          <div class="flex justify-end">
            <button class="bg-primary rounded px-6 py-3 text-white mt-6" @click="editItemPrice()" :loading="loadingItem">
              <i class="fas fa-check-circle"></i> Update Price
            </button>
          </div>
        </div>
      </modal>
  </div>
</template>
<script>
import { v2Api } from '@/http'
import { mapActions, mapState } from 'vuex'
import ErrorBag from '@/ErrorBag'
import DeletionModal from '@/components/Modals/DeletionModal'
export default {
  components: {
    DeletionModal
  },
  data () {
    return {
      selectedItem: null,
      showDeleteModal: false,
      selectedDiscountPriceType: null,
      selectedDiscount: null,
      discountForm: {
        type_id: null,
        price_type_id: null,
        value: null,
        notes: null,
        item_id: null
      },
      showEditPriceModal: false,
      errorBag: new ErrorBag(),
      loadingItem: false,
      originalItemQuote: 0,
      originalArtworkQuote: 0,
      originalDeliveryQuote: 0
    }
  },
  props: ['order', 'basketItems', 'updatingItem', 'discountType'],
  computed: {
    ...mapState(['profile_v2']),
    discountTypeName () {
      if (this.discountForm.price_type_id === null) {
        return ''
      }
      if (this.discountForm.price_type_id === 2) {
        return 'Delivery'
      } else if (this.discountForm.price_type_id === 3) {
        return 'Artwork'
      }
      return 'Item'
    },
    discountOriginalPrice () {
      if (this.selectedDiscountPriceType === 'item') {
        return this.originalItemQuote
      }
      if (this.selectedDiscountPriceType === 'artwork') {
        return this.originalArtworkQuote
      }
      if (this.selectedDiscountPriceType === 'delivery') {
        return this.originalDeliveryQuote
      }
    },
    discountLabel () {
      if (this.discountForm.type_id === 1) {
        return 'New Net Price (GBP)'
      }

      if (this.discountForm.type_id === 2) {
        return 'Percentage Discount'
      }

      return ''
    },
    calculatedDiscountPrice () {
      if (!this.orderItem || !this.discountOriginalPrice) {
        return 0
      }

      if (this.discountForm.type_id !== 2 || !this.discountForm.value) {
        return this.discountOriginalPrice
      }

      return this.discountOriginalPrice * ((100 - this.discountForm.value) / 100)
    },
    salesOrderId () {
      if (!this.orderItem || !this.orderItem.sales_order) {
        return null
      }

      return this.orderItem.sales_order.id || null
    }
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    viewItem (item) {
      this.$router.push(`/print-order-form/saved-items/${item.id}`)
    },
    viewEditPrice (priceType, item) {
      this.selectedDiscountPriceType = priceType
      this.discountForm.price_type_id = this.getPriceTypeId(priceType)
      this.discountForm.item_id = item.id
      this.originalItemQuote = item.item_quote
      this.originalArtworkQuote = item.artwork_quote
      this.originalDeliveryQuote = item.delivery_quote
      this.discountForm.type_id = null
      this.discountForm.value = null
      this.showEditPriceModal = true
    },
    getPriceTypeId (name) {
      if (name === 'item') {
        return 1
      }
      if (name === 'artwork') {
        return 3
      }
      if (name === 'delivery') {
        return 2
      }
    },
    editItemPrice () {
      if (this.saving) {
        return
      }
      this.errorBag.clear()
      this.saving = true
      v2Api.post(`/custom-print-order-items/${this.discountForm.item_id}/discounts`, this.discountForm).then((response) => {
        this.saving = false
        this.showEditPriceModal = false
        this.saving = false
        this.$emit('loadBasket')
        return this.displayToastNotification({
          type: 'success',
          icon: 'fa-check',
          title: 'Success',
          info: 'The selected price has been updated.'
        })
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.saving = false
      })
    }
  }
}
</script>
